<script setup lang="ts">
import { BarMitzvahType } from 'types/types'
import { computed, reactive, watch, watchEffect } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import { formattedBarMitzvah } from '@/composables/format'
import store from '@/store'

const logger = useLogger()

interface Props {
  barMitzvah?: BarMitzvahType | null
  barMitzvahClearable?: boolean
  disabled?: boolean
  externalSet?: boolean
  includeHidden?: boolean
  searchable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  barMitzvah: null,
  barMitzvahClearable: true,
  disabled: false,
  externalSet: false,
  includeHidden: false,
  searchable: true,
})

interface StudentSelect {
  selectedBarMitzvah: BarMitzvahType | null
}

const emit = defineEmits(['update:barMitzvah', 'change'])

const state = reactive<StudentSelect>({
  selectedBarMitzvah: props.barMitzvah,
})

// Emit immediately on any selection change — the parent (StudentToSentencesSelect)
// handles upgrading from the light version to the full bar mitzvah.
watch(
  () => state.selectedBarMitzvah,
  () => {
    logger.info('StudentSelect.watch state.selectedBarMitzvah')
    emit('update:barMitzvah', state.selectedBarMitzvah)
  },
  { deep: true, immediate: true },
)

// Sync from parent prop (for v-model two-way binding)
watchEffect(() => {
  state.selectedBarMitzvah = props.barMitzvah
})

const students = computed<Array<BarMitzvahType>>(() => {
  const source = props.includeHidden ? store.getters.teacher.lightBarMitzvot : store.getters.teacher.visibleLightBarMitzvot
  return [...source].sort((a, b) => (a.student.firstName ?? '').localeCompare(b.student.firstName ?? ''))
})

const onChange = () => {
  logger.info('StudentSelect.onchange')
  emit('change', state.selectedBarMitzvah)
}

function clear() {
  state.selectedBarMitzvah = null
}

defineExpose({ clear })
</script>

<template>
  <v-autocomplete
    v-model="state.selectedBarMitzvah"
    auto-select-first
    :clearable="barMitzvahClearable"
    :disabled="props.disabled"
    :item-title="
      (barMitzvah: BarMitzvahType) => {
        return formattedBarMitzvah(barMitzvah)
      }
    "
    :items="students"
    :label="state.selectedBarMitzvah ? 'Student' : 'Choose a student'"
    no-data-text="No students! Add a Student via the Students tab."
    return-object
    :searchable="searchable"
    variant="underlined"
    @update:model-value="onChange"
  />
</template>
